import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, CheckIcon, XMarkIcon, PhoneIcon, TrashIcon, ClockIcon, ChatBubbleLeftRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import type { Lead } from './leadsTypes';
import type { Run } from './leadsTypes';
import { TemperaturePill, DispositionBadge } from '../../components/intelligence';

type LeadsTableProps = {
  leads: Lead[];
  runs: Map<string, Run>;
  editingId: string | null;
  editData: Partial<Lead>;
  setEditData: (d: Partial<Lead>) => void;
  getRunName: (runId: string | null, created_at?: string, source?: string | null) => string;
  formatDateDDMMYYYY: (iso: string) => string;
  onEdit: (lead: Lead) => void;
  onSave: (leadId: string) => void;
  onCancel: () => void;
  onRunCampaignForLead: (lead: Lead) => void;
  onOpenDeleteConfirm: (lead: Lead) => void;
  onSendSms?: (lead: Lead) => void;
  onSendEmail?: (lead: Lead) => void;
  /** When false, Send email is disabled (platform email sender not configured or plan ineligible). */
  canSendEmail?: boolean;
  onViewHistory: (lead: Lead) => void;
  selectedLeadIds: Set<string>;
  isAllOnPageSelected: boolean;
  onToggleSelection: (lead: Lead) => void;
  onSelectAllOnPage: () => void;
  sortField: 'created_at' | 'name' | 'current_temperature' | 'current_disposition' | 'phone' | 'email' | 'source' | 'attempts' | 'last_contact_at';
  sortDirection: 'asc' | 'desc';
  onRequestSort: (field: 'created_at' | 'name' | 'current_temperature' | 'current_disposition' | 'phone' | 'email' | 'source' | 'attempts' | 'last_contact_at') => void;
};

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  runs,
  editingId,
  editData,
  setEditData,
  getRunName,
  formatDateDDMMYYYY,
  onEdit,
  onSave,
  onCancel,
  onRunCampaignForLead,
  onOpenDeleteConfirm,
  onSendSms,
  onSendEmail,
  canSendEmail = true,
  onViewHistory,
  selectedLeadIds,
  isAllOnPageSelected,
  onToggleSelection,
  onSelectAllOnPage,
  sortField,
  sortDirection,
  onRequestSort,
}) => {
  const navigate = useNavigate();
  const SortHeader = ({
    label,
    field,
    className,
  }: {
    label: string;
    field: 'created_at' | 'name' | 'current_temperature' | 'current_disposition' | 'phone' | 'email' | 'source' | 'attempts' | 'last_contact_at';
    className?: string;
  }) => (
    <th className={className}>
      <button
        type="button"
        onClick={() => onRequestSort(field)}
        className="inline-flex items-center gap-1 hover:text-cyan-300 transition-colors"
        title={`Sort by ${label}`}
      >
        {label}
        {sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
      </button>
    </th>
  );
  return (
  <div className="hidden lg:block overflow-x-auto scrollbar-hide">
    <table className="w-full table-fixed min-w-[900px] xl:min-w-0">
      <colgroup>
        <col className="w-10" />
        <col className="w-[110px] xl:w-[130px]" />
        <col className="w-[90px]" />
        <col className="w-[100px]" />
        <col className="w-[120px] xl:w-[140px]" />
        <col className="hidden xl:table-column w-[160px]" />
        <col className="w-[130px] xl:w-[180px]" />
        <col className="w-[70px]" />
        <col className="hidden xl:table-column w-[100px]" />
        <col className="w-[130px]" />
      </colgroup>
      <thead className="bg-white/5 border-b border-white/10">
        <tr>
          <th className="px-2 xl:px-3 py-3 text-left w-10">
            <label className="cursor-pointer inline-flex items-center justify-center relative w-5 h-5 rounded-md focus-within:ring-2 focus-within:ring-cyan-500/50 focus-within:ring-offset-2 focus-within:ring-offset-slate-900">
              <input
                type="checkbox"
                checked={isAllOnPageSelected}
                onChange={onSelectAllOnPage}
                className="peer sr-only"
                title={isAllOnPageSelected ? 'Deselect all on page' : 'Select all on page'}
              />
              <span className="absolute inset-0 w-5 h-5 rounded-md border-2 border-slate-500 bg-slate-800/80 transition-all duration-200 peer-checked:bg-cyan-500 peer-checked:border-cyan-400 hover:border-slate-400" />
              <CheckIcon className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none relative z-10" />
            </label>
          </th>
          <SortHeader label="Name" field="name" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Lead Temp" field="current_temperature" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Disposition" field="current_disposition" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Phone" field="phone" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Email" field="email" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider hidden xl:table-cell" />
          <SortHeader label="Source" field="source" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Attempts" field="attempts" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider" />
          <SortHeader label="Last Contact" field="last_contact_at" className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider hidden xl:table-cell" />
          <th className="px-2 xl:px-3 py-3 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {leads.map((lead) => {
          const isEditing = editingId === lead.id;
          const isSelected = selectedLeadIds.has(lead.id);
          return (
            <tr key={lead.id} className={`hover:bg-white/5 transition-colors ${isSelected ? 'bg-cyan-500/10' : ''}`}>
              <td className="px-2 xl:px-3 py-3">
                <label className="cursor-pointer inline-flex items-center justify-center relative w-5 h-5 rounded-md focus-within:ring-2 focus-within:ring-cyan-500/50 focus-within:ring-offset-2 focus-within:ring-offset-slate-900">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelection(lead)}
                    className="peer sr-only"
                    title={isSelected ? 'Deselect' : 'Select for campaign'}
                  />
                  <span className="absolute inset-0 w-5 h-5 rounded-md border-2 border-slate-500 bg-slate-800/80 transition-all duration-200 peer-checked:bg-cyan-500 peer-checked:border-cyan-400 hover:border-slate-400" />
                  <CheckIcon className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none relative z-10" />
                </label>
              </td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300">
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="truncate block max-w-full text-cyan-400 hover:text-cyan-300 hover:underline transition-colors text-left"
                    title={lead.name}
                  >
                    {lead.name}
                  </button>
                )}
              </td>
              <td className="px-2 xl:px-3 py-3">
                <TemperaturePill temperature={lead.current_temperature} />
              </td>
              <td className="px-2 xl:px-3 py-3">
                <DispositionBadge disposition={lead.current_disposition} />
              </td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300">
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.phone || ''}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                  />
                ) : (
                  <span className="truncate block max-w-full" title={lead.phone}>{lead.phone}</span>
                )}
              </td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300 hidden xl:table-cell">
                {isEditing ? (
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 text-sm"
                  />
                ) : (
                  <span className="truncate block max-w-full" title={lead.email || ''}>{lead.email || '-'}</span>
                )}
              </td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300">
                <span className="truncate block max-w-full" title={getRunName(lead.run_id, lead.created_at, lead.source)}>
                  {getRunName(lead.run_id, lead.created_at, lead.source)}
                </span>
              </td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300 text-center">{lead.total_attempts ?? lead.retry_count}</td>
              <td className="px-2 xl:px-3 py-3 text-sm text-gray-300 hidden xl:table-cell">{lead.last_contact_at ? formatDateDDMMYYYY(lead.last_contact_at) : '—'}</td>
              <td className="px-2 xl:px-3 py-3 text-sm">
                {isEditing ? (
                  <div className="flex items-center space-x-1">
                    <button type="button" onClick={() => onSave(lead.id)} className="p-1 text-green-400 hover:text-green-300 transition-colors" title="Save">
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={onCancel} className="p-1 text-red-400 hover:text-red-300 transition-colors" title="Cancel">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-0.5">
                    <button type="button" onClick={() => navigate(`/leads/${lead.id}`)} className="p-1 text-slate-400 hover:text-cyan-300 transition-colors" title="View lead details">
                      <ClockIcon className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => onEdit(lead)} className="p-1 text-cyan-400 hover:text-cyan-300 transition-colors" title="Edit">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => onRunCampaignForLead(lead)} className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors" title="Run campaign">
                      <PhoneIcon className="w-3.5 h-3.5" />
                    </button>
                    {onSendSms && (
                      <button type="button" onClick={() => onSendSms(lead)} className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors" title="Send SMS">
                        <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {onSendEmail && (lead.email || '').trim() && (
                      <button
                        type="button"
                        onClick={() => onSendEmail(lead)}
                        disabled={!canSendEmail}
                        className={`p-1 transition-colors ${canSendEmail ? 'text-sky-400 hover:text-sky-300' : 'text-sky-400/35 cursor-not-allowed'}`}
                        title={
                          canSendEmail
                            ? 'Send email'
                            : 'Email sending is not available on your plan'
                        }
                      >
                        <EnvelopeIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={() => onOpenDeleteConfirm(lead)} className="p-1 text-red-400 hover:text-red-300 transition-colors" title="Delete lead">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

export default LeadsTable;
